import { useLingui } from '@lingui/react/macro'
import { useCountryGroups } from '@/hooks/use-address-lists'
import { NativeSelect, NativeSelectOptGroup, NativeSelectOption } from '@/components/ui/native-select'
import { hasFieldError, useFieldContext } from './shared'
import { NativeSelectFieldShell } from './native-select-shell'

/**
 * `CountryField` as a native `<select>`, for a form someone fills with their OWN address: a combobox's `<button>`
 * is invisible to browser autofill. Values are ISO 3166-1 alpha-2, which the `country` token delivers. Costs the
 * flags and type-to-search. Pair with `NativeProvinceField`; this field's `listeners` clear the province.
 */
export default function NativeCountryField(props: {
  label: string
  className?: string
  placeholder?: string
  optional?: boolean
  orientation?: 'vertical' | 'horizontal'
  autoComplete?: string
}) {
  const { t } = useLingui()
  const field = useFieldContext<string>()
  const groups = useCountryGroups()
  const value = field.state.value ?? ''
  const selected = [...groups.priority, ...groups.other].find((o) => o.value === value)

  return (
    <NativeSelectFieldShell
      label={props.label}
      className={props.className}
      optional={props.optional}
      orientation={props.orientation}
      readOnlyLabel={selected?.label}
    >
      <NativeSelect
        className="w-full"
        id={field.name}
        name={field.name}
        autoComplete={props.autoComplete ?? 'country'}
        aria-invalid={hasFieldError(field.state.meta) || undefined}
        value={value}
        onBlur={field.handleBlur}
        onChange={(event) => field.handleChange(event.target.value)}
      >
        <NativeSelectOption value="">{props.placeholder ?? t`Select a country`}</NativeSelectOption>
        {/* Until the list lands, the stored code still needs an option or the select shows the placeholder. */}
        {value && !selected && <NativeSelectOption value={value}>{value}</NativeSelectOption>}
        {groups.priority.length > 0 && (
          <NativeSelectOptGroup label={t`Popular`}>
            {groups.priority.map((item) => (
              <NativeSelectOption key={item.value} value={item.value}>
                {item.label}
              </NativeSelectOption>
            ))}
          </NativeSelectOptGroup>
        )}
        {groups.other.length > 0 && (
          <NativeSelectOptGroup label={t`All countries`}>
            {groups.other.map((item) => (
              <NativeSelectOption key={item.value} value={item.value}>
                {item.label}
              </NativeSelectOption>
            ))}
          </NativeSelectOptGroup>
        )}
      </NativeSelect>
    </NativeSelectFieldShell>
  )
}
